import React, { useMemo, useState, type FC } from 'react';
import RegisterLayout from '@/registration/component/Register/Layout';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import { UiHStack, UiStack, UiText, UiButton, UiIconCaretLeft, UiIconCaretRight } from '@/lib/ui';
import PayByCard from './PayByCard';
import PayByInvoice from './PayByInvoice';
import { registration } from '@/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTenantApi } from '@/account/hook/useTenantApi';
import GroupHeader from '@/registration/component/Register/Layout/GroupHeader';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type paymentOptionType, processFreeOrder, type Order, type ProcessFreeOrderRequest } from '@/api/registration';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useNavigate } from 'react-router-dom';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { generatePageUrl } from '@/app/pages';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import { useGroupCartQuery } from '@/registration/hook/useGroupCartQuery';
import { createFormatPrice } from '@/lib/util/locale';
import { StandardSpinner } from '@/base/QueryContainer/QueryContainer';
import { useRegisterGroupSteps } from '@/registration/hook/useRegisterGroupSteps';
import InfoBox from '@/registration/component/InfoBox';
import BaseThemeButton from '@/base/ThemeButton';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';

const PRICE_ZERO = 0;
const Payment: FC = () => {
  const priceCurrency = 'AUD';
  const priceLocale = 'en-AU';
  const formatPrice = useMemo(
    () => { return createFormatPrice({ locale: priceLocale, currency: priceCurrency }); },
    []
  );
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { eventId, tenantCode } = useRegisterRoute();
  const [errors, setErrors] = useState<string[]>([]);
  const cartQuery = useGroupCartQuery(eventId);
  const navigate = useNavigate();
  const { toPreviousStep } = useRegisterGroupSteps();

  const { mutate: processFreeOrderMutate, isLoading: isProcessFreeOrderLoading } = useMutation<ApiResponseSingle<Order>, Error, ProcessFreeOrderRequest>({
    mutationFn: async (preparePaymentData: ProcessFreeOrderRequest) => {
      return await processFreeOrder(createTenantApiRequest)(preparePaymentData);
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        navigate(generatePageUrl('RegistrationRegisterRegistrationDetails', { tenantCode, eventId }, { orderHash: result.item.secureHash }));
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to completed registrations']);
    }
  });

  const totalPriceZero = cartQuery.data?.totalAmount === PRICE_ZERO;

  const onSubmitFreeOrder = () => {
    if (!cartQuery.data) {
      return;
    }
    const carInfo: ProcessFreeOrderRequest = {
      cartId: cartQuery.data.id
    };
    processFreeOrderMutate(carInfo);
  };

  const paymentOptionQuery = useQuery(
    [registration.paymentOptionQueryKey],
    async () => { return (await registration.loadPaymentOptions(createTenantApiRequest)({ eventId })).items; },
    { enabled: !isApiPreparing }
  );

  const checkHasPaymentGateway = useMemo(() => {
    return (optionName: paymentOptionType) => {
      return paymentOptionQuery.data?.find(option => { return option.name === optionName && option.enabled; });
    };
  }, [paymentOptionQuery.data]);

  if (cartQuery.isFetching && totalPriceZero) {
    return (
      <StandardSpinner />
    );
  }

  // Only show the error if there is an actual error.
  if (cartQuery.error && totalPriceZero) {
    return (
      <BaseMessageBarError>
        Failed to load the shopping cart.
      </BaseMessageBarError>
    );
  }

  return (
    <RegisterLayout header={<GroupHeader />}>
      {totalPriceZero ? (
        <UiHStack alignItems={'center'} justifyContent={'center'}>
          <UiText variant={'h6'}>
            Your cart adds up to {formatPrice(0)} so there is nothing to pay. Please click &quot;Complete&quot; to finish your registration.
          </UiText>
        </UiHStack>
      ) : (
        <BaseTransitionSlideLeft>
          <UiStack flexGrow={1} spacing={8}>
            <InfoBox
              title={'Choose payment method'}
            />
            <UiStack spacing={4}>
              { checkHasPaymentGateway('stripe') && <PayByCard creditCardGateway='stripe' />}
              { checkHasPaymentGateway('eWay') && <PayByCard creditCardGateway='eWay' />}
              { checkHasPaymentGateway('secure_pay') && <PayByCard creditCardGateway='secure_pay' />}
              { checkHasPaymentGateway('nab_transact') && <PayByCard creditCardGateway='nab_transact' />}
              { checkHasPaymentGateway('invoice') && <PayByInvoice />}
            </UiStack>
          </UiStack>
        </BaseTransitionSlideLeft>
      )}
      {errors.length > 0 && (
        <UiStack spacing={4} flexGrow={1} py={4}>
          {errors.map((error) => {
            return (
              <BaseMessageBarError key={error} mt={3}>{error}</BaseMessageBarError>
            );
          })}
        </UiStack>
      )}
      <UiStack height={96} />
      {totalPriceZero ? (
        <LayoutFooter isGroup>
          <UiHStack justifyContent={'space-between'} flexGrow={1} py={{base: 1, lg: 2}}>
            {/*<UiButton px={8} size={'lg'} colorScheme={'primary'} onClick={toPreviousStep} variant={'ghost'}>*/}
            {/*        Previous*/}
            {/*</UiButton>*/}
            <UiButton
              size={'lg'}
              colorScheme={'primary'}
              onClick={toPreviousStep}
              variant={'ghost'}
              fontSize={'lg'}
              leftIcon={(
                <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>
              )}
            >
              Prev
            </UiButton>

            {/*<UiButton px={8} size={'lg'} shadow={'base'} colorScheme={'primary'} onClick={onSubmitFreeOrder} isLoading={isProcessFreeOrderLoading}>*/}
            {/*        Complete*/}
            {/*</UiButton>*/}
            <UiButton
              size={'lg'}
              colorScheme={'primary'}
              onClick={onSubmitFreeOrder}
              isLoading={isProcessFreeOrderLoading}
              variant={'ghost'}
              fontSize={'lg'}
              rightIcon={(
                <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>
              )}
            >
              Complete
            </UiButton>
          </UiHStack>
        </LayoutFooter>
      ) : (
        <LayoutFooter isGroup>
          <UiHStack justifyContent={'space-between'} flexGrow={1}>
            {/*<UiButton*/}
            {/*  size={'lg'}*/}
            {/*  colorScheme={'primary'}*/}
            {/*  onClick={toPreviousStep}*/}
            {/*  variant={'ghost'}*/}
            {/*  fontSize={'lg'}*/}
            {/*  leftIcon={(*/}
            {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
            {/*  )}*/}
            {/*>*/}
            {/*  Previous*/}
            {/*</UiButton>*/}

            <BaseThemeButton
              theme={'gray'}
              onClick={toPreviousStep}
              leftIcon={(
                <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
              )}
            >
              Prev
            </BaseThemeButton>
            <BrandFooter/>
            <UiStack width={24}/>

            {/*<UiButton px={8} size={'lg'} colorScheme={'primary'} onClick={toPreviousStep} variant={'ghost'}>*/}
            {/*          Previous*/}
            {/*</UiButton>*/}
          </UiHStack>
        </LayoutFooter>
      )}
    </RegisterLayout>
  );
};

export default Payment;
